import { fundingCountryCodes } from './country-codes.js';
import { customerReportingCurrency, extendedKycBody, extendedKycFields, isKycCountryField, validExtendedKycField, } from './customer-kyc-extended.js';
const groups = {
    basicDetails: ['firstName', 'lastName', 'dateOfBirth', 'nationality'],
    residentialAddress: ['street', 'locality', 'postalCode', 'country', 'administrativeArea'],
};
export function customerKycFields(verification) {
    const fields = [];
    let unsupported = false;
    for (const [category, requirement] of Object.entries(verification.requirements)) {
        if (requirement.status !== 'incomplete')
            continue;
        if (!requirement.requiredFields?.length) {
            unsupported = true;
            continue;
        }
        for (const field of requirement.requiredFields) {
            const extra = extendedKycFields(category, field);
            if (extra)
                fields.push(...extra);
            else if ((category === 'basicDetails' || category === 'residentialAddress') &&
                groups[category].some((allowed) => allowed === field))
                fields.push(field);
            else
                unsupported = true;
        }
    }
    return {
        fields: [...new Set(fields)].filter((field) => field !== 'reportingCountry' || !fields.includes('country')),
        unsupported,
    };
}
export function buildCustomerKyc(verification, draft) {
    if (verification.status !== 'collecting' || verification.challenge)
        throw new Error('kyc_not_collecting');
    const { fields } = customerKycFields(verification);
    if (!validCustomerKycDraft(fields, draft))
        throw new Error('kyc_invalid_fields');
    const body = extendedKycBody(fields, draft);
    for (const category of ['basicDetails', 'residentialAddress']) {
        const requested = fields.filter((field) => groups[category].some((allowed) => allowed === field));
        if (requested.length)
            body[category] = Object.fromEntries(requested.map((field) => [field, draft[field]?.trim()]));
    }
    return body;
}
export function validKycField(field, input) {
    const value = input.trim();
    const extended = validExtendedKycField(field, value);
    if (extended !== undefined)
        return extended;
    if (isKycCountryField(field))
        return fundingCountryCodes.some(({ alpha3 }) => alpha3 === value);
    if (field === 'dateOfBirth')
        return validBirthDate(value);
    if (field === 'administrativeArea')
        return value.length > 0;
    if (field === 'firstName' || field === 'lastName' || field === 'locality')
        return value.length >= (field === 'locality' ? 3 : 1) && /^[^0-9<>&"/]+$/.test(value);
    return value.length >= 3 && /^[^<>&"]+$/.test(value);
}
function validBirthDate(value) {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(value))
        return false;
    const year = Number(value.slice(0, 4));
    const month = Number(value.slice(5, 7));
    const day = Number(value.slice(8, 10));
    const leap = year % 4 === 0 && (year % 100 !== 0 || year % 400 === 0);
    const days = [31, leap ? 29 : 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
    return (year > 0 &&
        month >= 1 &&
        month <= 12 &&
        day >= 1 &&
        day <= (days[month - 1] ?? 0) &&
        value <= new Date().toISOString().slice(0, 10));
}
export async function submitCustomerKyc(provider, verification, draft) {
    const result = await provider.submitCustomerKyc(verification.customerId, buildCustomerKyc(verification, draft));
    if (!result.ok) {
        // A competing verification may have started; re-read instead of replaying PII.
        if (result.error.code === 'conflict')
            return refreshCustomerKyc(provider, verification.customerId);
        throw new CustomerKycError(result.error.code);
    }
    return verifiedCustomer(result.value.data, verification.customerId);
}
export async function refreshCustomerKyc(provider, customerId) {
    const result = await provider.getCustomer(customerId);
    if (!result.ok)
        throw new CustomerKycError(result.error.code);
    return verifiedCustomer(result.value.data, customerId);
}
function verifiedCustomer(customer, expectedId) {
    if (customer.id !== expectedId)
        throw new Error('kyc_customer_mismatch');
    return {
        customerId: customer.id,
        status: customer.kyc.status,
        requirements: customer.kyc.requirements,
        challenge: customer.kyc.challenge ?? null,
    };
}
export class CustomerKycError extends Error {
    code;
    constructor(code) {
        super(code);
        this.code = code;
        this.name = 'CustomerKycError';
    }
}
export function validCustomerKycDraft(fields, draft) {
    return (fields.length > 0 &&
        fields.every((field) => validKycField(field, draft[field] ?? '')) &&
        (!fields.includes('reportingCurrency') ||
            draft.reportingCurrency ===
                customerReportingCurrency((fields.includes('country') ? draft.country : draft.reportingCountry) ?? '')));
}
